import type { PlayCandidate, PlayId } from '../../api/plays';
import { displayCompanyName } from './labels';

/** Legal forms and filler dropped from a company name before grouping, so "CGI FRANCE" ≡ "CGI France". */
const NAME_NOISE = new Set([
  'bv', 'nv', 'sa', 'sas', 'sasu', 'sarl', 'eurl', 'gmbh', 'mbh', 'ag', 'kg', 'kgaa', 'ohg', 'ug', 'se', 'co', 'ltd', 'limited',
  'llc', 'llp', 'lp', 'inc', 'corp', 'plc', 'srl', 'spa', 'bvba', 'cvba', 'vof', 'ab', 'oy', 'as', 'aps', 'pty', 'pte', 'the',
]);

function companyKey(name: string): string {
  return displayCompanyName(name)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    // Dotted legal forms read as one word: "B.V." → "bv", "S.A." → "sa".
    .replace(/\b([a-z])\.(?=[a-z]\.?)/g, '$1')
    .replace(/\./g, '')
    .split(/[^a-z0-9]+/)
    .filter((t) => t && !NAME_NOISE.has(t))
    .join(' ');
}

/**
 * Country and region words a group's local entities add to the parent brand:
 * "SoftwareONE Deutschland" and "SoftwareONE Netherlands" are one company to
 * work, so they share a block. Group/holding words go too.
 */
const ENTITY_WORDS = new Set([
  'deutschland', 'germany', 'netherlands', 'nederland', 'holland', 'france', 'belgium', 'belgie', 'belgique',
  'belux', 'benelux', 'luxembourg', 'uk', 'gb', 'ireland', 'italia', 'italy', 'espana', 'spain', 'iberia',
  'portugal', 'austria', 'osterreich', 'schweiz', 'switzerland', 'suisse', 'nordic', 'nordics', 'sweden',
  'sverige', 'norway', 'norge', 'denmark', 'danmark', 'finland', 'suomi', 'poland', 'polska', 'czech', 'europe',
  'emea', 'international', 'global', 'group', 'groupe', 'holding', 'holdings',
  // Practice and service-line words a firm's entities add: "Deloitte Consulting GmbH",
  // "DELOITTE CONSEIL", "Deloitte IP GmbH", "Deloitte Consultative Services B.V."
  // and "Deloitte Ireland LLP" are one firm to work.
  'consulting', 'consultants', 'consultancy', 'consultative', 'conseil', 'conseils', 'beratung', 'advisory',
  'advisors', 'advisers', 'services', 'service', 'ip', 'tax', 'legal', 'audit', 'assurance', 'financial',
  'partners', 'associes',
  // A staffing group's local entities: "Randstad Uitzendbureau b.v." and
  // "Randstad Professional Solutions GmbH & Co. KG" are Randstad (Oct 2026).
  'professional', 'professionals', 'solutions', 'uitzendbureau',
]);

/** The parent brand: the company key without country, region and group words. Null when nothing distinctive is left. */
export function brandKey(name: string): string | null {
  const tokens = companyKey(name).split(' ').filter((t) => t && !ENTITY_WORDS.has(t));
  const key = tokens.join(' ');
  return key.length >= 3 ? key : null;
}

/** The company's domain without scheme and www, when known. */
export function domainKeyOf(domain: string | null): string | null {
  if (!domain) return null;
  const d = domain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');
  return d.includes('.') ? d : null;
}

export function personKeyOf(row: PlayCandidate): string | null {
  const raw = row.personLinkedinUrl ?? row.personEmail;
  if (!raw || row.needsContact) return null;
  return raw.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');
}

/** One person in a company group: the candidate the buttons act on, plus the same person proposed under another entity name. */
export interface PersonLine {
  primary: PlayCandidate;
  dupes: PlayCandidate[];
}

/** One company within one play: every entity name it was proposed under and each person once. */
export interface CompanyGroup {
  id: string;
  playId: PlayId;
  names: string[];
  first: PlayCandidate;
  lines: PersonLine[];
  lastSeenAt: string;
}

/**
 * Rows → one group per company per play. Two rows belong together when their
 * names match without legal forms, when they share a company domain, when
 * they share a parent brand once country, group and practice words are dropped ("SoftwareONE Deutschland" ≡
 * "SoftwareONE Netherlands"), or when they propose the same person (the
 * Contacts tab lists the CGI people under "CGI", "CGI FRANCE" and "CGI
 * Deutschland"). Each person is shown once; the duplicates stay attached.
 */
export function groupByCompany(rows: PlayCandidate[]): CompanyGroup[] {
  const parent = rows.map((_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i]!)));
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[Math.max(ra, rb)] = Math.min(ra, rb);
  };
  const seen = new Map<string, number>();
  rows.forEach((r, i) => {
    const brand = brandKey(r.companyName);
    const domain = domainKeyOf(r.companyDomain);
    for (const k of [
      `n:${r.playId}:${companyKey(r.companyName)}`,
      brand ? `b:${r.playId}:${brand}` : null,
      domain ? `d:${r.playId}:${domain}` : null,
      personKeyOf(r) ? `p:${r.playId}:${personKeyOf(r)}` : null,
    ]) {
      if (!k) continue;
      const j = seen.get(k);
      if (j === undefined) seen.set(k, i);
      else union(i, j);
    }
  });
  const groups = new Map<number, CompanyGroup>();
  rows.forEach((r, i) => {
    const root = find(i);
    let g = groups.get(root);
    if (!g) {
      g = { id: r.id, playId: r.playId, names: [], first: r, lines: [], lastSeenAt: r.lastSeenAt };
      groups.set(root, g);
    }
    if (!g.names.includes(displayCompanyName(r.companyName))) g.names.push(displayCompanyName(r.companyName));
    if (r.lastSeenAt > g.lastSeenAt) g.lastSeenAt = r.lastSeenAt;
    const pk = personKeyOf(r);
    const line = pk ? g.lines.find((l) => personKeyOf(l.primary) === pk) : undefined;
    if (line) line.dupes.push(r);
    else g.lines.push({ primary: r, dupes: [] });
  });
  // A group with named people does not also need its "needs a contact" row.
  for (const g of groups.values()) {
    if (g.lines.some((l) => !l.primary.needsContact)) g.lines = g.lines.filter((l) => !l.primary.needsContact);
  }
  return [...groups.values()];
}
